import type { GQL_ID } from '@@partner:types:graphql'
import type { AddressSuggestion, GeoPoint } from '@@partner:types:app'

import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { mergeLeftOnly, mergeLeft } from 'merge-left-utils'
import { commonFieldChange, getCurrencySign } from '@@partner:helpers/util'
import { useFetchShop } from '@@partner:graphql/query/shop/shop.gql'
import { useUpdateShopInfo } from '@@partner:graphql/mutation/shop/updateShopInfo.gql'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import {
  validateAddressSuggestion,
  normalizeAddressSuggestion
} from '@@partner:helpers/address'
import {
  makeGraphQLErrorNotification,
  notifyOk,
  notifyError
} from '@@partner:helpers/notification'
import {
  makeDefaultState,
  mapStateToApiVariable,
  mapApiDataToState
} from './ShopFormState'
import {
  representTimeWithDateObject,
  convertDateObjectToSeconds
} from '@@partner:helpers/date-utils'

import ValidationModel from './validation/ShopFormValidationModel'

import {
  Form,
  SelectPicker,
  CheckPicker,
  InputNumber,
  Panel,
  Message,
  Grid,
  Col,
  Row
} from 'rsuite'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import AppLoader from '@components/common/AppLoader'
import { SaveButton } from '@components/common/form/Buttons'
import TimePicker from '@components/common/TimePicker'
import CustomerAddress from '@components/order/form/CustomerAddress'
import ShopTimezonePicker from './form/ShopTimezonePicker'

import SlotTableContainer from './form/slot/SlotTableContainer'

import {
  SHOP_STATUS_LABELS
} from '@@partner:config/shop'

import ShopMarker from './form/maps/leaflet/ShopMarker'
import FormCheckbox from '@components/common/form/FormCheckbox'

import './ShopForm.scss'
import ShopBoxes from './form/ShopBoxes'
import { generatePath, useHistory } from 'react-router-dom'
import { ROUTE_SHOPS_EDIT_SHOP } from '@@partner:config/routes'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import CustomerPhone from '@components/order/form/CustomerPhone'
import { Pane } from 'react-leaflet'
import ShopImages from './form/ShopImages'
import { useFetchTopology } from '../../graphql/query/topology.gql'
import ShopTagsPicker from './form/ShopTagsPicker'
import ShopDeliveryZonesTable from './form/ShopDeliveryZonesTable'

export interface ShopFormProps {
  shopId: GQL_ID
  brandSlug: string
}

export default function ShopForm ({ shopId, brandSlug }: ShopFormProps) {
  const history = useHistory()
  const { formState, setFormState, setFormRef, setFormErrors } =
    useValidatableForm(() => makeDefaultState(shopId))

  const { data, loading: loadingShopInfo, error } = useFetchShop({ shopId })

  const [ formReady, setFormReady ] = useState(false)
  const [ selectedChannels, setSelectedChannels ] = useState<string[]>([])

  const [
    updateShopInfo,
    { loading: loadingUpdateShopInfo, error: errorUpdatingShopInfo }
  ] = useUpdateShopInfo(shopId)

  const { data: topologyData, loading: loadingTopology } = useFetchTopology()

  const cityOptions = useMemo(() => {
    if (!topologyData?.topology?.countries) return []
    return topologyData.topology.countries.flatMap((country: any) =>
      country.regions.flatMap((region: any) =>
        region.cities.map((city: any) => ({
          label: `${city.name} (${region.name}, ${country.name})`,
          value: city.id
        }))
      )
    )
  }, [ topologyData ])

  // set initial from backend
  useEffect(() => {
    if (!loadingShopInfo && data) {
      setFormState((state) => mergeLeft(state, mapApiDataToState(data?.shop)))
      const shop = data?.shop
      if (shop) {
        const channels: string[] = []
        if (shop.tg_chat_id) channels.push('telegram')
        if (shop.vkMaxChatId) channels.push('vkmax')
        setSelectedChannels(channels)
      }
      setFormReady(true)
    }
  }, [ data, loadingShopInfo, setFormState ])

  
  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        updateShopInfo({
          variables: {
            shopInfo: mapStateToApiVariable(formState),
            upsert: shopId ? false : true
          }
        })
          .then(({ data }) => {
            if (shopId) {
              notifyOk('Магазин обновлён')
              if (data?.shopInfo) {
                setFormState((state) => mergeLeft(state, mapApiDataToState(data?.shopInfo)))                
              }
            }
            else if (data?.shopInfo) {
              notifyOk('Магазин создан')
              const url = generatePath(ROUTE_SHOPS_EDIT_SHOP, {
                brandSlug,
                shopId: data?.shopInfo.id
              })
              history.replace(url)
            }
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ formState, setFormState, updateShopInfo, shopId, brandSlug, history ]
  )

  const handleAddressChosen = (suggestion: AddressSuggestion) => {
    if (validateAddressSuggestion(suggestion)) {
      if (suggestion.data.geo_lat && suggestion.data.geo_lon) {
        const shopGeoPoint: GeoPoint = {
          latitude: parseFloat(suggestion.data.geo_lat),
          longitude: parseFloat(suggestion.data.geo_lon)
        }

        const updateState = mergeLeftOnly(
          [ 'address' ],
          {
            address: '',
            shopGeoPoint
          },
          normalizeAddressSuggestion(suggestion)
        )

        setFormState((state) => mergeLeft(state, updateState))
      }
    }
  }

  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFieldChange = commonFieldChange(setFormState)

  const handleFormError = useCallback(
    (error: any) => {
      setFormErrors(error)
    },
    [ setFormErrors ]
  )

  const handleStatusChange = useCallback(
    (status: number | string) => {
      const statusInt = typeof status === 'string' ? parseInt(status) : status
      setFormState((state) => mergeLeft(state, { status: statusInt }))
    },
    [ setFormState ]
  )

  const handleTimeChange = useCallback(
    (field: string, seconds: number) => {
      setFormState((state) =>
        mergeLeft(state, { [field]: seconds })
      )
    },
    [ setFormState ]
  )

  const handleChangeShopGeoPosition = useCallback(
    (shopGeoPoint) => {
      setFormState((formState) => ({
        ...formState,
        shopGeoPoint // { longitude: e.latLng.lat(), latitude: e.latLng.lng() }
      }))
    },
    [ setFormState ]
  )

  const handleShopTzOffsetChange = useCallback(
    (tzOffset) => setFormState((formState) => ({ ...formState, tzOffset })),
    [ setFormState ]
  )

  const handleSlotChange = useCallback(
    (slotPlans) => {
      setFormState((formState) => ({ ...formState, slotPlans }))
    },
    [ setFormState ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные магазина #${shopId} для редактирования`}
        error={error}
      />
    )
  }

  if (loadingShopInfo) {
    return (
      <AppLoader.RowsWithText content="Загружаем зоны доставки других магазинов" />
    )
  }

  return (
    <Form
      model={ValidationModel}
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      className="ShopForm"
    >
      {loadingShopInfo && <AppLoader.Inner />}

      {!!errorUpdatingShopInfo && (
        <GraphQLCommonErrorDisplay
          title={'Не удалось сохранить данные магазина'}
          error={errorUpdatingShopInfo}
        />
      )}
      <Grid fluid>
        <Col xs={24} md={12}>
          <Panel
            shaded
            header='Общая информация'
          >
            <Row className='my-5'>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Название</Form.ControlLabel>
                  <Form.Control name="name" placeholder="Название" />
                </Form.Group> 
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Адрес</Form.ControlLabel>
                  <CustomerAddress
                    onAddressChosen={handleAddressChosen}
                    placeholder="Укажите адрес магазина"
                    noSearchBtn
                    name="address"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className='my-5'>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Часовой пояс</Form.ControlLabel>
                  <ShopTimezonePicker
                    value={formState.tzOffset || 0}
                    onChange={handleShopTzOffsetChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Время работы</Form.ControlLabel>
    
                  <TimePicker
                    value={representTimeWithDateObject(formState.dateOpenTime)}
                    onSelect={(date) => handleTimeChange('dateOpenTime', date)}
                  />
                  <span>-</span>
                  <TimePicker
                    value={representTimeWithDateObject(formState.dateCloseTime)}
                    onSelect={(date) => handleTimeChange('dateCloseTime', date)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className='my-5'>
              <Form.Group>
                <Form.ControlLabel>Статус</Form.ControlLabel>
                <SelectPicker
                  data={SHOP_STATUS_LABELS}
                  labelKey="label"
                  valueKey="value"
                  value={formState.status}
                  onChange={(status) => handleStatusChange?.(status === null ? 0 : status)}
                  cleanable={false}
                  searchable={false}
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Номер телефона</Form.ControlLabel>
                <CustomerPhone name='phone'/>
              </Form.Group>
            </Row>
            <Row className='my-5'>
              <Col xs={12}>
                <Form.Group>
                  <Form.ControlLabel>Город</Form.ControlLabel>
                  <SelectPicker
                    data={cityOptions}
                    value={formState.city_id}
                    onChange={value => setFormState(state => ({ ...state, city_id: value }))}
                    placeholder="Выберите город"
                    block
                    disabled={loadingTopology}
                    name="city_id"
                    searchable
                  />
                </Form.Group>
              </Col>
            </Row>
          </Panel>
          <Panel
            header='Сервисы в магазине'
            shaded
          >
            <ShopTagsPicker 
              selectedTags={formState.shopTags} 
              onChange={tags => setFormState(state => ({ ...state, shopTags: tags }))} 
            />
          </Panel>
          
        </Col>
        <Col xs={24} md={12}>
          <Panel shaded>
            <ShopMarker
              geoPoint={formState.shopGeoPoint}
              shop={data?.shop}
              onPositionChanged={handleChangeShopGeoPosition}
            />
          </Panel>
        </Col>
        <Row>
          <Col xs={24}>
            <Panel
              shaded
              header='Фотогалерея'
              className='my-5'
            >
              <ShopImages setFormState={setFormState} formState={formState} />
            </Panel>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Panel
              shaded
              header='Настройка самовывоза'
              className='my-5'
            >
              <Row className='my-5'>
                <Col xs={24}>
                  <Form.Group>
                    <Form.ControlLabel>
                        Самовывоз включен
                    </Form.ControlLabel>
                    <FormCheckbox.Control name="has_pickup_delivery_enabled" />
                  </Form.Group>
                </Col>
                { formState.has_pickup_delivery_enabled && (
                  <Row>
                    <Col md={12} lg={6}>
                      <Form.Group>
                        <Form.ControlLabel>
                          Минимальная корзина {getCurrencySign()}
                        </Form.ControlLabel>
                        <Form.Control
                          accepter={InputNumber}
                          min={0}
                          step={0}
                          name="minOrderCost"
                          placeholder="Минимальная стоимость заказа"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} lg={6}>
                      <Form.Group>
                        <Form.ControlLabel>
                            Стоимость самовывоза
                        </Form.ControlLabel>
                        <Form.Control
                          accepter={InputNumber}
                          min={0}
                          step={1}
                          name="pickup_delivery_cost"
                          placeholder="Стоимость самовывоза"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} lg={6} className="my-2">
                      <Form.Group>
                        <Form.ControlLabel>Обслуживает розницу</Form.ControlLabel>
                        <FormCheckbox.Control name="is_serving_retail" />
                      </Form.Group>
                    </Col>
                    <Col md={12} lg={6} className="my-2">
                      <Form.Group>
                        <Form.ControlLabel>Обслуживает опт</Form.ControlLabel>
                        <FormCheckbox.Control name="is_serving_wholesale" />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Row>
              

              { formState.has_pickup_delivery_enabled && (
                <SlotTableContainer
                  value={formState.slotPlans}
                  onChange={handleSlotChange}
                >
                  <Message showIcon className="mt-2">
                    Слоты будут создаваться во временной зоне магазина!
                  </Message>
                </SlotTableContainer>
              )}
            </Panel>
          </Col>
        </Row>
        <Row>
          <Col xs={24} md={24}>
            <Panel 
              header="Технические настройки" 
              shaded
              className='my-5'
            >
              <Row>
                <Col>
                  <Form.Group>
                    <Form.ControlLabel>Имя для URL</Form.ControlLabel>
                    <Form.Control
                      name="slug"
                      placeholder="Например: gulliver-skochilova"
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.ControlLabel>ID в ТУС</Form.ControlLabel>
                    <Form.Control name="xid" placeholder="ID в ТУС" />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mt-4">
                <Form.ControlLabel>Каналы уведомлений</Form.ControlLabel>
                <CheckPicker
                  style={{ width: 300 }}
                  searchable={false}
                  placeholder="Выберите каналы"
                  data={[
                    { label: 'Telegram', value: 'telegram' },
                    { label: 'VK MAX', value: 'vkmax' }
                  ]}
                  value={selectedChannels}
                  onChange={(channels: string[]) => {
                    setSelectedChannels(channels)
                    if (!channels.includes('telegram')) {
                      setFormState((prev: any) => ({ ...prev, tg_chat_id: '' }))
                    }
                    if (!channels.includes('vkmax')) {
                      setFormState((prev: any) => ({ ...prev, vkMaxChatId: '' }))
                    }
                  }}
                />
              </Form.Group>
              {selectedChannels.includes('telegram') && (
                <Form.Group className="mt-3">
                  <Form.ControlLabel>ID чата Telegram</Form.ControlLabel>
                  <Form.Control style={{ width: 300 }} name="tg_chat_id" placeholder="ID чата Telegram" />
                </Form.Group>
              )}
              {selectedChannels.includes('vkmax') && (
                <Form.Group className="mt-3">
                  <Form.ControlLabel>ID чата VK MAX</Form.ControlLabel>
                  <Form.Control style={{ width: 300 }} name="vkMaxChatId" placeholder="ID чата VK MAX" />
                </Form.Group>
              )}
            </Panel>
          </Col>
        </Row>
        <Row>
          <Col xs={24} md={24}>
            <Panel
              shaded
              header='Ячейки'
              className='my-5'
            >
              <ShopBoxes 
                shopId={shopId}
                boxes={formState.deliveryBoxes}
                onChange={handleFieldChange('deliveryBoxes')}
              />
            </Panel>
          </Col>
        </Row>
        <Row>
          <Panel
            shaded
            header='Доставка'
            className='my-5'
          >
            <Col xs={24}>
              <Form.Group className='mb-5'>
                <Form.ControlLabel>Сообщение курьерам</Form.ControlLabel>
                <FormTextareaControl rows={3} name='deliveryDefaultPickupComment' placeholder='Сообщение курьеру'/>
              </Form.Group>
            </Col>
          </Panel>
        </Row>
        {shopId && (
          <Row>
            <Col xs={24}>
              <Panel
                shaded
                header='Зоны доставки'
                className='my-5'
              >
                <ShopDeliveryZonesTable shopId={shopId} />
              </Panel>
            </Col>
          </Row>
        )}
      </Grid>
      <SaveButton
        loading={loadingShopInfo || loadingUpdateShopInfo}
        disabled={loadingShopInfo || loadingUpdateShopInfo}
      >
        Cохранить изменения
      </SaveButton>
    </Form>
  )
}
